import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";

import { translateUntyped } from "../../common/Dictionary";

/**
 * Generic hook to create a function that translates an ID to a name using data from a react-query result.
 *
 * @template TQueryData The type of the data returned by the react-query hook.
 * @template TItem The type of the items in the array extracted from TQueryData. Must have idKey and nameKey properties.
 * @param {UseQueryResult<TQueryData, unknown>} queryResult The result object from a `useQuery` hook.
 * @param {(data: TQueryData | undefined) => TItem[] | undefined} dataExtractor A function to extract the relevant array of items from the query data.
 * @param {keyof TItem} idKey The key in TItem that holds the ID.
 * @param {keyof TItem} nameKey The key in TItem that holds the name to be translated.
 * @returns {(id: number | string | undefined) => string | number} A function that takes an ID and returns the translated name or the original ID if not found, loading, or error.
 */
export const useIdToNameTranslator = <
  TQueryData,
  TItem extends Record<string, unknown> & Record<K, number> & Record<N, string>,
  K extends keyof TItem,
  N extends keyof TItem,
>(
  queryResult: UseQueryResult<TQueryData, unknown>,
  dataExtractor: (data: TQueryData | undefined) => TItem[] | undefined,
  idKey: K,
  nameKey: N,
): ((id: number | undefined) => string | number) => {
  return useMemo(() => {
    return (id: number | undefined): string | number => {
      if (id === undefined) {
        return "N/A";
      }

      if (queryResult.isLoading) {
        return id;
      }

      if (queryResult.isError) {
        return "Error";
      }

      const items = dataExtractor(queryResult.data);
      if (!items) {
        return id;
      }

      const item = items.find((i) => i[idKey] === Number(id));

      return item ? (translateUntyped(item[nameKey]) ?? id) : id;
    };
  }, [queryResult.data, queryResult.isLoading, queryResult.isError, dataExtractor, idKey, nameKey]);
};
